import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Grid, FormControlLabel, Typography, Link } from '@material-ui/core';

import { PaperlessModal, DisclosureModal } from './components';
import { CheckBox } from 'components';
import { TERMS_DETAILS_REPLACE_MACROS } from 'Views/Common/constants';
import { GROUP_SERVICES } from 'Services';
import { Context } from "context";
import { CONDITIONS_APPLY_UPDATE, CONDITIONS_APPLY_RESET } from "context/actions";
import fetchService from 'Services/getApidataStrapi';
import { STRAPI_API_DISCLOSURE_STATEMENT_RECUR_EN } from 'Routes/Main/constants';
import { PAYMENT_TYPES_NAME } from 'Views/Common/enum';

const SubscriberTerms = (props) => {
  const {
    type: termsType = 'recur',
    dataValues: { group_id: groupId } = {},
    modalView = false,
    showCancellationPolicy = true,
    countryConfig = {},
    paymentType = '',
    selectedPlan,
    settingsData
  } = props;

  const {
    state: {
      conditionsApply: {
        termsConditions = false,
        cancellationPolicy = false,
        notificationConsent = false,
        bacsPaymentConsent = false,
      } = {},
    },
    dispatch: globalDispatch,
  } = useContext(Context);

  const [isOpenPopup, setIsOpenPopup] = useState(false);
  const [disclosureLoading, setDisclosureLoading] = useState(true);
  const [updatedContent, setUpdatedContent] = useState();
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [disclosureData, setDisclosureData] = useState();
  const [disclosureDetails, setDisclosureDetails] = useState();

  const isRecurProduct = termsType === 'recur';

  const openPopup = (type) => setIsOpenPopup(type);
  const closePopup = () => setIsOpenPopup(false);

  const setConditionsApply = (name, value) =>
    globalDispatch({
      type: CONDITIONS_APPLY_UPDATE,
      payload: {
        name,
        value,
      },
    });

  const resetConditionsApply = () =>
    globalDispatch({
      type: CONDITIONS_APPLY_RESET,
    });


  const getStrapiData = async (apiPath) => {
    try {
      const response = await fetchService(apiPath);
      return response;
    } catch (err) {
      console.error('Strapi API Error:', err);
      throw err;
    }
  };

  const updateDisclosureContent = (disclosureDetails) => {
    const {
      group_name = '',
      group_url = '',
      email: group_email,
      max_additional_members: group_members = 0,
      patient_website = '',
      industry = '',
      patient_support_email = '',
    } = disclosureDetails;

    if (disclosureData) {
      // Create the proper structure for the modal with replaced content
      const processedContent = disclosureData
        .replace(
          new RegExp(TERMS_DETAILS_REPLACE_MACROS.group_name, 'g'),
          group_name,
        )
        .replace(
          new RegExp(TERMS_DETAILS_REPLACE_MACROS.group_url, 'g'),
          group_url,
        )
        .replace(
          new RegExp(TERMS_DETAILS_REPLACE_MACROS.group_email, 'g'),
          group_email,
        )
        .replace(
          new RegExp(TERMS_DETAILS_REPLACE_MACROS.group_members, 'g'),
          group_members,
        )
        .replace(
          new RegExp(TERMS_DETAILS_REPLACE_MACROS.group_patient_portal, 'g'),
          patient_website,
        )
        .replace(
          new RegExp(TERMS_DETAILS_REPLACE_MACROS.group_industry, 'g'),
          industry.toLowerCase(),
        )
        .replace(
          new RegExp(
            TERMS_DETAILS_REPLACE_MACROS.subscribili_support_email,
            'g',
          ),
          patient_support_email,
        )
        .replaceAll(
          TERMS_DETAILS_REPLACE_MACROS.group_phone,
          settingsData?.level_data?.phone || '',
        )
        .replaceAll(
          TERMS_DETAILS_REPLACE_MACROS.group_support_phone,
          settingsData?.level_data?.phone || '',
        )
      // Create the structure expected by DisclosureModal
      const formattedContent = {
        title: {
          rendered: `Terms and Conditions for ${group_name} Subscribers`,
        },
        content: {
          rendered: processedContent,
        },
      };

      setUpdatedContent(formattedContent);
    }
  };

  const getDisclosureDetails = async () => {
    if (!groupId || groupId === 'ALL_GROUPS') return;

    try {
      const response = await GROUP_SERVICES.getDisclosureDetails(groupId);
      if (response?.type === 'success' && response?.data) {
        updateDisclosureContent(response.data);
        setDisclosureDetails(response.data);
      } else {
        throw response;
      }
    } catch (err) {
      console.log(err);
    }
    setDetailsLoading(false);
  };

  useEffect(() => {
    disclosureData && groupId && getDisclosureDetails();
  }, [groupId, disclosureData]);

  const getDisclosureStatement = async () => {
    try {
      const strapiResponse = await getStrapiData(
        STRAPI_API_DISCLOSURE_STATEMENT_RECUR_EN,
      );
      setDisclosureData(strapiResponse?.data?.description);
    } catch (err) {
      console.log(err);
    }
    setDisclosureLoading(false);
  };

  useEffect(() => {
    getDisclosureStatement();

    return () => resetConditionsApply();
  }, []);

  const getLinkText = (text = 'Terms & Conditions') => {
    if (!groupId) return text;
    return (
      <Link
        href={`/patient-terms?group_id=${groupId}&phone=${settingsData?.level_data?.phone || ''}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        <Typography
          className="cursor_pointer"
          component="span"
          color="secondary"
          disabled={!groupId}
          style={{ fontSize: '12px' }}
        >
          {text}
        </Typography>
      </Link>
    )
  };

  const getPrivacyLinkText = (text = 'Privacy Policy') => {
    if (!groupId) return text;
    return (
      <Link
        href={`/patient-privacy?group_id=${groupId}&plan_name=${encodeURIComponent(selectedPlan?.plan_name || '')}`}
        target="_blank"
        rel="noopener noreferrer"
        disabled={!groupId}
        onClick={(e) => e.stopPropagation()} // Prevent checkbox toggle
        data-testid="checkout-privacy-link"
      >
        <Typography
          className="cursor_pointer"
          component="span"
          color="secondary"
          style={{ fontSize: '12px' }}
        >
          {text}
        </Typography>
      </Link>
    );
  };




  const getTermsConditionsText = () => {
    if (isRecurProduct)
      if (countryConfig?.code === 'US') {
        return (
          <Typography style={{ fontSize: '12px', marginTop: '8px' }}>
            The primary member has reviewed and agreed to the subscription service’s {getLinkText('Terms & Conditions')}, including the automatic renewal and Cancellation Policy.</Typography>
        );
      }
      else {
        return (
          <Typography style={{ fontSize: '12px', marginTop: '8px' }}>
            The Primary Member consents to their personal data being shared with Subscribili for the management of the Plan, as stated in the {getPrivacyLinkText('Privacy Policy')}, and agrees to the Subscription {getLinkText('Terms and Conditions')}, including renewal, cancellation, and refund policies.
          </Typography>
        );
      }

    return (
      <>
        Member has reviewed and accepted the {getLinkText()} as well as the
        cancellation policies.
      </>
    );
  };


  const getNotificationConsentText = () => {
    return (
      <Typography style={{ fontSize: '12px', marginTop: '8px' }}>
        The Primary Member also consents to receiving SMS and email notifications from Subscribili in accordance with the {getPrivacyLinkText('Privacy Policy')}.
      </Typography>
    );
  };

  const getBacsDebitConsentText = () => {
    return (
      <Typography style={{ fontSize: '12px', marginTop: '8px' }}>
        The Primary Member understands that Stripe will collect Direct Debits on
        behalf of the {disclosureDetails?.group_name} and confirms that they are the
        account holder and the sole person authorised to approve debits from
        this account.
      </Typography>
    );
  }
  const paperlessText = isRecurProduct
    ? 'Paperless communication has been enabled for your subscription.'
    : 'Paperless communication has been enabled for your order(s).';

  return (
    <>
      {showCancellationPolicy && (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <CheckBox
                checked={cancellationPolicy}
                onClick={() =>
                  setConditionsApply('cancellationPolicy', !cancellationPolicy)
                }
                name="cancellationPolicy"
                inputProps={{ 'data-testid': `checkout-cancellation-checkbox` }}
                disabled={!groupId}
              />
            }
            label={<Typography variant="h6">Cancellation Policy</Typography>}
          />
          <Typography variant="body2">
            Services will be charged at full rates if subscription is cancelled
            within the subscription term. Refunds will be granted if the
            cancellation is made within the specified refund window and no
            services have been provided. Restrictions and exceptions may apply,
            please review{' '}
            <Link
              onClick={() => groupId && openPopup('View_Disclosure')}
              data-testid="checkout-disclosure-link"
            >
              <Typography
                className="cursor_pointer"
                component="span"
                color="secondary"
              >
                T&C
              </Typography>
            </Link>{' '}
            for details.
          </Typography>
        </Grid>
      )}

      <Grid item xs={12}>
        {countryConfig?.code !== 'US' &&
          paymentType === PAYMENT_TYPES_NAME.BACS_Debit && (
            <FormControlLabel
              control={
                <CheckBox
                  checked={bacsPaymentConsent}
                  onClick={() => {
                    setConditionsApply(
                      'bacsPaymentConsent',
                      !bacsPaymentConsent,
                    );
                  }}
                  name="bacsPaymentConsent"
                  inputProps={{
                    'data-testid': `checkout-bacs-payment-checkbox`,
                  }}
                  disabled={!groupId}
                />
              }
              label={getBacsDebitConsentText()}
              style={{ alignItems: 'flex-start', marginRight: 0 }}
            />
          )}
        <FormControlLabel
          control={
            <CheckBox
              checked={termsConditions}
              onChange={(e) => setConditionsApply('termsConditions', e.target.checked)}
              name="termsConditions"
              inputProps={{ "data-testid": `checkout-terms-checkbox` }}
              disabled={!groupId}
            />
          }
          label={getTermsConditionsText()}
          style={{ alignItems: 'flex-start', marginRight: 0 }}
        />
        {countryConfig?.code !== 'US' && (
          <FormControlLabel
            control={
              <CheckBox
                checked={notificationConsent}
                onClick={() => {
                  setConditionsApply(
                    'notificationConsent',
                    !notificationConsent,
                  );
                }}
                name="termsConditions"
                inputProps={{ 'data-testid': `checkout-terms-checkbox` }}
                disabled={!groupId}
              />
            }
            label={getNotificationConsentText()}
            style={{ alignItems: 'flex-start', marginRight: 0 }}
          />
        )}
      </Grid>

      {!modalView && countryConfig?.code === 'US' && (
        <Typography
          variant="body2"
          component="span"
          className="mt-10 color_success"
        >
          {/* <Link onClick={() => openPopup('Paperless')} className={clsx(style.cursor_pointer, "color_success")} data-testid='checkout-paperless-link'>
            Paperless {" "}
          </Link>{" "} */}
          🌱 {paperlessText}
        </Typography>
      )}

      {isOpenPopup === 'Paperless' && (
        <PaperlessModal isOpen={true} closePopup={closePopup} />
      )}

      {(isOpenPopup === 'Disclosure' || isOpenPopup === 'View_Disclosure') && (
        <DisclosureModal
          isOpen={true}
          showButton={isOpenPopup === 'Disclosure'}
          closePopup={closePopup}
          emitSuccessEvent={() => {
            closePopup();
            setConditionsApply('termsConditions', true);
          }}
          isLoading={disclosureLoading || detailsLoading}
          disclosureContent={updatedContent}
        />
      )}
    </>
  );
};

SubscriberTerms.propTypes = {
  type: PropTypes.string,
  dataValues: PropTypes.object,
  modalView: PropTypes.bool,
  showCancellationPolicy: PropTypes.bool,
  countryConfig: PropTypes.object,
  selectedPlan: PropTypes.shape({
    plan_name: PropTypes.string,
  }),
  settingsData: PropTypes.shape({
    level_data: PropTypes.shape({
      phone: PropTypes.string,
    }),
  }),
};

export default SubscriberTerms;
